import { getCountryConfig, getCountryConfigByPlanGroup } from '@ctct/domain-layer';

import { createPlanComparisonContentStrategy } from './services/createPlanComparisonContentStrategy';
import { PlanComparisonTableService } from './services/PlanComparisonTableService';
import type { Init } from './types';
import { managerHelpers } from '../../helpers';
import type { MergedDataReturnType, MergedHandlersReturnType } from '../../types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

export type PlanComparisonTableData = MergedDataReturnType<[PlanComparisonTableService]>;

export type PlanComparisonTableHandlers = MergedHandlersReturnType<[PlanComparisonTableService]>;

type TApi = ReturnType<PlanComparisonTableService['api']>;

export class PlanComparisonTableManager extends WithApi<TApi> {
  public planComparisonTableInit = ({ queries, t, flags = {} }: Init) => {
    const [
      planGroupQuery,
      billingAccountQuery,
      planDetailQuery,
      paymentPlanQuery,
      suspensionInfoQuery,
      customerQuery,
      addOnsQuery,
    ] = queries;

    const pending =
      planGroupQuery.isPending ||
      billingAccountQuery.isPending ||
      planDetailQuery.isPending ||
      paymentPlanQuery.isPending ||
      suspensionInfoQuery?.isLoading ||
      customerQuery.isPending;
    const error =
      planGroupQuery.error ||
      billingAccountQuery.error ||
      planDetailQuery.error ||
      paymentPlanQuery.error ||
      suspensionInfoQuery?.error ||
      customerQuery.error;

    if (pending) {
      return {
        status: 'pending',
        data: null,
        handlers: null,
        lifecycle: {} as WidgetLifecycle,
      } as const;
    }

    if (error) {
      return {
        status: 'error',
        data: error,
        handlers: {
          retry: () => {
            managerHelpers.retryQueries(queries);
          },
        },
        lifecycle: {} as WidgetLifecycle,
      } as const;
    }

    // Resolve market config from plan group first so content strategy selection and SMS
    // pricing use the same country-config source (AU 338, GB 340/341). See specs/sms-international.md.
    const planGroupCountryConfig = getCountryConfigByPlanGroup(planDetailQuery.data.getPlanGroupId());
    const contentStrategy = createPlanComparisonContentStrategy(flags, planGroupCountryConfig);

    // Fall back to CustomerInfo country code when plan group does not map to a market config,
    // so SMS pricing/copy renders from centralized country-config. See CBU-21106 plan §2/§4a.
    const countryConfig = planGroupCountryConfig ?? getCountryConfig(customerQuery.data.getCountryCode());

    // addOnsQuery is intentionally excluded from pending/error gates: SMS pricing is omitted
    // while addOns is loading or when getAddOns fails; otherwise resolved from API tiers only.
    const planComparisonTable = new PlanComparisonTableService(
      planGroupQuery.data,
      billingAccountQuery.data,
      paymentPlanQuery.data,
      t,
      contentStrategy,
      flags,
      countryConfig,
      suspensionInfoQuery?.data,
      addOnsQuery.data
    );

    this.initApi({
      ...planComparisonTable.api(),
    });

    return {
      status: 'loaded',
      data: {
        ...planComparisonTable.data(),
      },
      handlers: {
        ...planComparisonTable.handlers(),
      },
      lifecycle: {
        onMount() {
          planComparisonTable.onMount();
        },
      } as WidgetLifecycle,
    } as const;
  };
}
